/**
 * AI 问题分析 JSON 格式数据渲染器
 * 
 * 功能说明：
 * 本组件专门用于直接渲染 JSON 格式的 AI 分析结果，不再经过 JSON -> Markdown 的转换。
 * 相比旧的 Markdown 渲染方式，JSON 渲染方式可以更精确地控制各个数据字段的展示效果，
 * 便于后续扩展更多交互功能。
 * 
 * 数据结构：
 * JSON 数据结构定义在 ai-issue-analysis.ts 中，包含 5 个步骤：
 * - step1InitialHypotheses: 生成初始假设集
 * - step2HypothesisEvaluation: 评估假设置信度
 * - step3DeepExploration: 深度探索高置信假设
 * - step4Conclusion: 根因结论
 * - step4Conclusion.fixRecommendations: 修复建议（作为第5步展示）
 * 
 * 交互功能：
 * - 假设的展开/折叠
 * - 深度分析输入和提交
 * - 补充假设功能
 * - 引用日志链接（跳转到日志位置）
 */
import React, { memo, useMemo, useState } from 'react';
import gfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import ReactMarkdown from 'react-markdown';
import { Button, Divider, Image, Input, message, Tooltip } from 'antd';
import { CS_STYLES } from 'utils/constants/style-constants';
import CsCollapseButton from 'components/commons/CsCollapseButton/CsCollapseButton';
import DeepAnalysisIcon from 'svg/v2/newcs_aiassistant_deepanalysis_icon.svg';
import { isZh, ze } from 'utils/zhEn';
import { ErrorBoundary } from 'react-error-boundary';
import RootCauseIcon from 'svg/v2/newcs_aiassistant_rootcauseanalysis_icon.svg';
import SuggestionIcon from 'svg/v2/newcs_aiassistant_repairsuggestions_icon.svg';
import CsSimpleButton from 'components/antd-extension/CsSimpleButton';
import { AiDrawerHooks } from 'components/top/AiDrawer/ai-drawer-hooks';
import { LinkOutlined } from '@ant-design/icons';
import { AiIssueAnalysisUtil } from 'components/top/AiDrawer/ai-issue-analysis';
import scss from './AiDrawer.scss';

// pre-wrap 允许在空格和 <wbr> 处换行。normal 表示不从单词中间拆开。
const codeWrapStyle = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'normal',
  overflowWrap: 'normal',
  maxWidth: '100%',
};

/** 片段之间插入 <wbr>。断点规则见 splitCodeForSoftWrap。 */
function withCodeSoftWraps(children) {
  return React.Children.map(children, (child, childIndex) => {
    if (typeof child !== 'string') {
      return child;
    }
    const parts = AiIssueAnalysisUtil.splitCodeForSoftWrap(child);
    return parts.map((part, index) => (
      <React.Fragment key={`${childIndex}-${index}`}>
        {index > 0 && <wbr />}
        {part}
      </React.Fragment>
    ));
  });
}

// Markdown 组件配置：用于渲染 JSON 字段中的 Markdown 内容
const markdownComponents = {
  img({ node, ...props}) {
    return <div style={{ maxWidth:'100%' }}>
      <Image src={props.src} />
    </div>
  },
  th: ({ node, ...props }) => (
    <th style={{ border: '1px solid #aaa', padding: '8px', textAlign: 'left' }} {...props}>
      {props.children}
    </th>
  ),
  td: ({ node, ...props }) => (
    <td style={{ border: '1px solid #aaa', padding: '8px' }} {...props}>
      {props.children}
    </td>
  ),
  code: ({ node, inline, children, ...props }) => (
    <code
      {...props}
      style={{ ...(props.style || {}), ...codeWrapStyle, display: inline ? undefined : 'block' }}
    >
      {withCodeSoftWraps(children)}
    </code>
  ),
  pre: ({ node, ...props }) => (
    <pre
      {...props}
      style={{
        ...(props.style || {}),
        maxWidth: '100%',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'normal',
        overflowWrap: 'normal',
      }}
      onScroll={(e) => e.stopPropagation()}
    />
  ),
};

/**
 * 配置 rehype-sanitize 的安全白名单
 * 添加自定义标签 'quoted-log-btn' 用于渲染引用日志的链接按钮
 */
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'quoted-log-btn'],
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes['*'] || []), 'className', 'style']
  },
};

/**
 * 单个假设的渲染组件（用于 Step1 和 Step2）
 * 
 * 功能：
 * - 展示假设标题和内容（证据、推理、平台相关性等）
 * - 支持折叠/展开
 * - 支持深度分析：点击图标可展开输入框，用户可补充思路后提交进行深入分析
 * - 自动检测内容中的引用日志，添加链接按钮
 */
const HypothesisBlock = ({
  hypothesis,
  quotedLogs,
  enableDeepAnalysis,
  onClickDeepAnalysis = () => {},
  contextualMarkdownComponents,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isShowDeepAnalysisInput, setIsShowDeepAnalysisInput] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const guessTitle = `${ze('假设', 'Hypothesis ')}${hypothesis.id}: [${hypothesis.type}]`;

  /**
   * 检测内容中是否包含引用日志，如果包含则在末尾添加引用按钮标记
   * 这些标记会被 ReactMarkdown 的自定义组件渲染为可点击的链接按钮
   */
  const enrichContent = (content) => {
    if (!content) return content;
    const matchedQuotedLogIndex = quotedLogs?.findIndex(({ log }) => log && content.includes(log));
    if (matchedQuotedLogIndex >= 0) {
      return AiIssueAnalysisUtil.appendQuotedLogButton(content, matchedQuotedLogIndex);
    }
    return content;
  };

  // 构建内容 Markdown
  let contentMarkdown = `- ${ze('证据', 'Evidence')}\n\n`;
  contentMarkdown += `  ${hypothesis.evidence}\n\n`;
  if (hypothesis.evidenceData) {
    contentMarkdown += AiIssueAnalysisUtil.markdownFence(hypothesis.evidenceData);
  }
  contentMarkdown += `- ${ze('推理', 'Reasoning')}: ${hypothesis.reasoning}`;
  if (hypothesis.platformRelevance) {
    contentMarkdown += ` - ${ze('平台/机型相关性', 'Platform Relevance')}: ${hypothesis.platformRelevance}`;
  }

  // 处理引用日志
  const enrichedContent = enrichContent(contentMarkdown);

  const contentDom = <ReactMarkdown
    remarkPlugins={[gfm]}
    rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
    linkTarget='_blank'
    children={enrichedContent}
    components={contextualMarkdownComponents}
  />;

  return <div style={{ marginBottom: '8px' }}>
    <div
      style={{ backgroundColor: '#eceff5', padding: '4px 8px', borderRadius: '2px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}
    >
      <div>{ guessTitle }</div>
      <div style={{ flexGrow: 1 }}/>
      { enableDeepAnalysis && <div
        style={{ display: 'flex', alignItems: 'center',  cursor: 'pointer' }}
        onClick={() => setIsShowDeepAnalysisInput(!isShowDeepAnalysisInput)}
      >
        <Tooltip title={ze('基于此深度分析', 'Deep Analysis')}>
        <DeepAnalysisIcon style={{ color: CS_STYLES.getPrimaryColor() }}/>
        </Tooltip>
      </div> }
      <div style={{ transform: 'scale(1.25)', display: 'flex', alignItems: 'center'}}><CsCollapseButton
        isSmallSize={true}
        isCollapsed={collapsed}
        onClick={() => setCollapsed(!collapsed)}
      /></div>
    </div>
    { !collapsed && contentDom }
    { enableDeepAnalysis && isShowDeepAnalysisInput && <>
      <div><Input.TextArea
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        placeholder={ze('补充相关思路和依据信息', 'Input your relevant ideas and proofs.')}
      /></div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', gap: '16px' }}>
        <Button
          type='primary'
          size='small'
          onClick={() => {
            const v = inputValue.trim();
            const queryText = v
              ? ze(`针对 ${guessTitle} 进行深入分析，并补充相关思路和依据信息如下：\n${v}`, `Conduct in-depth analysis on ${guessTitle} with additional relevant ideas and evidence as follows:\n${v}`)
              : ze(`针对 ${guessTitle} 进行深入分析`, `Conduct in-depth analysis on ${guessTitle}`);
            setInputValue('');
            setIsShowDeepAnalysisInput(false);
            onClickDeepAnalysis(queryText);
          }}
        >{ ze('补充分析', 'Submit') }</Button>
      </div>
    </> }
  </div>;
};

/**
 * 手动补充假设的渲染组件（用于 Step1）
 * 
 * 功能：
 * - 在 Step1 的假设列表末尾显示
 * - 允许用户补充 AI 未列出的假设
 * - 用户可输入自己的假设和依据，然后提交进行深度分析
 */
const AddNewHypothesisBlock = ({
  enableDeepAnalysis,
  onClickDeepAnalysis = () => {},
}) => {
  const [isShowDeepAnalysisInput, setIsShowDeepAnalysisInput] = useState(false);
  const [inputValue, setInputValue] = useState('');

  return <div style={{ marginBottom: '8px' }}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ flexGrow: 1 }} />
      <span style={{ color: CS_STYLES.SUB_TEXT_COLOR, fontSize: '12px', marginRight: isZh() ? '0' : '4px' }}>{ ze('如果未包含你认为的假设，点击这里', 'If not contains your hypothesis, click here to') }</span>
      <Button
        type='link'
        style={{ padding: '0', fontSize: '12px' }}
        onClick={() => setIsShowDeepAnalysisInput(!isShowDeepAnalysisInput)}
      >{ ze('补充假设', 'add hypothesis') }</Button>
    </div>
    { enableDeepAnalysis && isShowDeepAnalysisInput && <>
      <div><Input.TextArea
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        placeholder={ze('补充你认为的假设和依据信息，并基于此深度分析', 'Add your hypothesis and evidence, then conduct deep analysis based on it')}
      /></div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', gap: '16px' }}>
        <Button
          type='primary'
          size='small'
          onClick={() => {
            const v = inputValue.trim();
            if (!v) {
              message.warn(ze('未输入假设信息', 'Please input hypothesis information'));
              return;
            }
            const queryText = ze(`请针对以下新增假设，进行深入分析：\n${v}`, `Please conduct in-depth analysis on the following new hypothesis:\n${v}`);
            setInputValue('');
            setIsShowDeepAnalysisInput(false);
            onClickDeepAnalysis(queryText);
          }}
        >{ ze('补充分析', 'Submit') }</Button>
      </div>
    </> }
  </div>;
};

/**
 * Step 1: 生成初始假设集
 * 
 * 渲染 JSON 数据中的 step1InitialHypotheses 字段
 * 包含多个假设项，每个假设包括类型、证据、推理、平台相关性等信息
 * 
 * 交互功能：
 * - 每个假设支持折叠/展开
 * - 每个假设支持深度分析（仅当 enableDeepAnalysis=true 时）
 * - 支持补充新假设
 */
const Step1InitialHypotheses = ({
  hypotheses,
  quotedLogs,
  enableDeepAnalysis,
  onClickDeepAnalysis,
  contextualMarkdownComponents,
}) => {
  return (
    <div style={{ display: 'flex' }}>
      {/* 左侧序号圆球和时间线 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: '12px'
      }}>
        {/* 序号圆球 */}
        <div style={{
          width: '32px',
          height: '32px',
          backgroundColor: CS_STYLES.getPrimaryColor(),
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 'bold',
          flexShrink: 0,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          1
        </div>

        <div style={{
          width: '2px',
          backgroundColor: '#d9d9d9',
          flexGrow: 1,
          minHeight: '20px'
        }} />
      </div>

      {/* 右侧内容 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{
          fontWeight: 'bold',
          fontSize: '18px',
          marginTop: '3px',
          marginBottom: '12px',
          color: '#333',
          lineHeight: '1.4'
        }}>
          {ze('生成初始假设集', 'Generate Initial Hypothesis Set')}
        </div>

        {/* 假设列表 */}
        <div>
          {hypotheses?.map(hypothesis => (
            <HypothesisBlock
              key={hypothesis.id}
              hypothesis={hypothesis}
              quotedLogs={quotedLogs}
              enableDeepAnalysis={enableDeepAnalysis}
              onClickDeepAnalysis={onClickDeepAnalysis}
              contextualMarkdownComponents={contextualMarkdownComponents}
            />
          ))}
          {enableDeepAnalysis && <AddNewHypothesisBlock
            enableDeepAnalysis={enableDeepAnalysis}
            onClickDeepAnalysis={onClickDeepAnalysis}
          />}
        </div>
      </div>
    </div>
  );
};

/**
 * 单个评估项的渲染组件（用于 Step2）
 * 
 * 功能：
 * - 展示假设的评分和评估理由
 * - 支持折叠/展开
 * - 自动检测内容中的引用日志，添加链接按钮
 */
const EvaluationBlock = ({
  evaluation,
  hypothesis,
  quotedLogs,
  contextualMarkdownComponents,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const guessTitle = `${ze('假设', 'Hypothesis')}${evaluation.hypothesisId}${hypothesis ? `: [${hypothesis.type}]` : ''}${ze('：评分：', ': Score: ')}${evaluation.score}`;

  // 构建内容 Markdown
  let contentMarkdown = `- ${ze('理由', 'Reason')}: ${evaluation.reason}`;
  if (evaluation.platformRelevance) {
    contentMarkdown += ` - ${ze('平台相关性', 'Platform Relevance')}: ${evaluation.platformRelevance}`;
  }

  // 处理引用日志
  const matchedQuotedLogIndex = quotedLogs?.findIndex(({ log }) => log && contentMarkdown.includes(log));
  if (matchedQuotedLogIndex >= 0) {
    contentMarkdown = AiIssueAnalysisUtil.appendQuotedLogButton(contentMarkdown, matchedQuotedLogIndex);
  }

  const contentDom = <ReactMarkdown
    remarkPlugins={[gfm]}
    rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
    linkTarget='_blank'
    children={contentMarkdown}
    components={contextualMarkdownComponents}
  />;

  return <div style={{ marginBottom: '8px' }}>
    <div
      style={{ backgroundColor: '#eceff5', padding: '4px 8px', borderRadius: '2px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}
    >
      <div>{ guessTitle }</div>
      <div style={{ flexGrow: 1 }}/>
      <div style={{ transform: 'scale(1.25)', display: 'flex', alignItems: 'center'}}><CsCollapseButton
        isSmallSize={true}
        isCollapsed={collapsed}
        onClick={() => setCollapsed(!collapsed)}
      /></div>
    </div>
    { !collapsed && contentDom }
  </div>;
};

/**
 * Step 2: 评估假设置信度
 * 
 * 渲染 JSON 数据中的 step2HypothesisEvaluation 字段
 * 包含对各个假设的评分、理由和 Top3 假设列表
 * 
 * 特点：
 * - 评估项按评分从高到低排序
 * - 每个评估项支持折叠/展开
 */
const Step2HypothesisEvaluation = ({
  evaluation,
  hypotheses,
  quotedLogs,
  contextualMarkdownComponents,
}) => {
  const { evaluations, top3Hypotheses } = evaluation || {};

  // 按评分排序
  const sortedEvaluations = useMemo(() => {
    return [...(evaluations || [])].sort((a, b) => b.score - a.score);
  }, [evaluations]);

  return (
    <div style={{ display: 'flex' }}>
      {/* 左侧序号圆球和时间线 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: '12px'
      }}>
        {/* 序号圆球 */}
        <div style={{
          width: '32px',
          height: '32px',
          backgroundColor: CS_STYLES.getPrimaryColor(),
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 'bold',
          flexShrink: 0,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          2
        </div>

        <div style={{
          width: '2px',
          backgroundColor: '#d9d9d9',
          flexGrow: 1,
          minHeight: '20px'
        }} />
      </div>

      {/* 右侧内容 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{
          fontWeight: 'bold',
          fontSize: '18px',
          marginTop: '3px',
          marginBottom: '12px',
          color: '#333',
          lineHeight: '1.4'
        }}>
          {ze('评估假设置信度', 'Hypothesis Evaluation')}
        </div>

        {/* 评估列表 */}
        <div>
          {sortedEvaluations?.map(ev => {
            const hypothesis = hypotheses?.find(h => h.id === ev.hypothesisId);
            return (
              <EvaluationBlock
                key={ev.hypothesisId}
                evaluation={ev}
                hypothesis={hypothesis}
                quotedLogs={quotedLogs}
                contextualMarkdownComponents={contextualMarkdownComponents}
              />
            );
          })}

          {top3Hypotheses && top3Hypotheses.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <ReactMarkdown
                remarkPlugins={[gfm]}
                linkTarget='_blank'
                children={`Top3 ${ze('假设', 'Hypotheses')}: [${ze('假设', 'Hypothesis')}${top3Hypotheses.join(` > ${ze('假设', 'Hypothesis')}`)}]`}
                components={markdownComponents}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Step 3: 深度探索高置信假设
 * 
 * 渲染 JSON 数据中的 step3DeepExploration 字段
 * 包含对高置信假设的深入分析：推演路径、根因分析、关键证据等
 * 
 * 内容包括：
 * - 深度探索的假设类型
 * - 推演路径（多个步骤，用 "→ Why?" 连接）
 * - 底层 why（根因分析）
 * - 关键证据
 * - 待验证操作
 * - 平台相关性
 */
const Step3DeepExploration = ({
  exploration,
  hypotheses,
  quotedLogs,
  contextualMarkdownComponents,
}) => {
  const exploredHypothesis = hypotheses?.find(h => h.id === exploration?.hypothesisExplored);

  let content = '';
  
  if (exploredHypothesis) {
    content += `${ze('深度探索假设', 'Deep Exploration Hypothesis')}: ${exploredHypothesis.type}\n\n`;
  }

  if (exploration?.reasoningPath && exploration.reasoningPath.length > 0) {
    content += `${ze('推演路径', 'Reasoning Path')}:\n\n`;
    exploration.reasoningPath.forEach((step, index) => {
      content += `${index + 1}. ${step}\n`;
      if (index < exploration.reasoningPath.length - 1) {
        content += '   → Why?\n';
      }
    });
    content += '\n';
  }

  if (exploration?.rootCauseAnalysis) {
    content += `${ze('底层why', 'Root Why')}:\n\n${exploration.rootCauseAnalysis}\n\n`;
  }

  if (exploration?.keyEvidence && exploration.keyEvidence.length > 0) {
    content += `${ze('关键证据', 'Key Evidence')}:\n\n`;
    exploration.keyEvidence.forEach(evidence => {
      content += `${evidence}\n`;
    });
    if (exploration.evidenceData && exploration.evidenceData.length > 0) {
      exploration.evidenceData.forEach(data => {
        content += AiIssueAnalysisUtil.markdownFence(data);
      });
    }
    content += '\n';
  }

  if (exploration?.verificationSteps && exploration.verificationSteps.length > 0) {
    content += `${ze('待验证操作', 'Verification Steps')}:\n\n`;
    exploration.verificationSteps.forEach((step, index) => {
      content += `${index + 1}. ${step}\n`;
    });
    content += '\n';
  }

  if (exploration?.platformRelevance) {
    content += `${ze('平台相关性', 'Platform Relevance')}:\n\n${exploration.platformRelevance}\n`;
  }

  // 检测引用日志
  const matchedQuotedLogIndex = quotedLogs?.findIndex(({ log }) => log && content.includes(log));
  if (matchedQuotedLogIndex >= 0) {
    content = AiIssueAnalysisUtil.appendQuotedLogButton(content, matchedQuotedLogIndex);
  }

  return (
    <div style={{ display: 'flex' }}>
      {/* 左侧序号圆球和时间线 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: '12px'
      }}>
        {/* 序号圆球 */}
        <div style={{
          width: '32px',
          height: '32px',
          backgroundColor: CS_STYLES.getPrimaryColor(),
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 'bold',
          flexShrink: 0,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          3
        </div>

        <div style={{
          width: '2px',
          backgroundColor: '#d9d9d9',
          flexGrow: 1,
          minHeight: '20px'
        }} />
      </div>

      {/* 右侧内容 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{
          fontWeight: 'bold',
          fontSize: '18px',
          marginTop: '3px',
          marginBottom: '12px',
          color: '#333',
          lineHeight: '1.4'
        }}>
          {ze('深度探索高置信假设', 'Deep Exploration of High-Confidence Hypothesis')}
        </div>

        {/* 内容 */}
        <div style={{ paddingBottom: '8px', lineHeight: '1.6' }}>
          <ReactMarkdown
            remarkPlugins={[gfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
            linkTarget='_blank'
            children={content}
            components={contextualMarkdownComponents}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Step 4: 根因结论
 * 
 * 渲染 JSON 数据中的 step4Conclusion 字段
 * 展示最终的根因结论和相关分析
 * 
 * 内容包括：
 * - 最可能根因（高亮显示在橙色背景中）
 * - 底层 why 归纳
 * - 证据链
 * - 关键代码定位（文件、函数、崩溃点）
 * - 机型相关性分析
 * - 日志分析
 * - 代码变更信息（作者、日期、提交信息、变更代码等）- 放在最后
 * 
 * 视觉特点：
 * - 使用 RootCauseIcon 图标
 * - 最可能根因使用橙色背景高亮（#ffd8d2）
 * - 代码变更信息使用淡黄色背景（#fff4e6），字段名加粗显示
 */
const Step4Conclusion = ({
  conclusion,
  quotedLogs,
  contextualMarkdownComponents,
}) => {
  let content = '';

  if (conclusion?.underlyingReason) {
    content += `${ze('底层why归纳', 'Underlying Why Summary')}:\n${conclusion.underlyingReason}\n\n`;
  }

  if (conclusion?.evidenceChain && conclusion.evidenceChain.length > 0) {
    content += `${ze('证据链', 'Evidence Chain')}:\n`;
    conclusion.evidenceChain.forEach((evidence, index) => {
      content += `${index + 1}. ${evidence.description}\n`;
    });
    content += '\n';
  }

  if (conclusion?.codeLocation) {
    content += `${ze('关键代码定位', 'Key Code Location')}:\n`;
    content += `${ze('文件', 'File')}: ${conclusion.codeLocation.file}\n`;
    content += `${ze('函数', 'Function')}: ${conclusion.codeLocation.function}\n`;
    content += `${ze('崩溃点', 'Crash Point')}: ${conclusion.codeLocation.crashPoint}\n\n`;
  }

  if (conclusion?.platformRelevance) {
    content += `${ze('机型相关性分析', 'Device Model Relevance Analysis')}:\n${conclusion.platformRelevance}\n\n`;
  }

  if (conclusion?.logAnalysis) {
    content += `${ze('日志分析', 'Log Analysis')}:\n${conclusion.logAnalysis}\n`;
  }

  // 检测引用日志
  const matchedQuotedLogIndex = quotedLogs?.findIndex(({ log }) => log && content.includes(log));
  if (matchedQuotedLogIndex >= 0) {
    content = AiIssueAnalysisUtil.appendQuotedLogButton(content, matchedQuotedLogIndex);
  }

  /**
   * 渲染代码变更信息（使用 DOM 而不是 Markdown）
   * 使用淡黄色背景，字段名加粗显示
   */
  const renderResponsibleChange = (change) => {
    if (!change) return null;

    return (
      <div style={{
        backgroundColor: '#fff4e6',
        borderRadius: '4px',
        padding: '12px',
        marginTop: '8px',
        marginBottom: '8px',
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
          {ze('代码变更信息', 'Code Change Information')}
        </div>
        
        {change.author && (
          <div style={{ marginBottom: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>{ze('作者', 'Author')}: </span>
            <span>{change.author}</span>
          </div>
        )}
        
        {change.date && (
          <div style={{ marginBottom: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>{ze('日期', 'Date')}: </span>
            <span>{change.date}</span>
          </div>
        )}
        
        {change.commitMessage && (
          <div style={{ marginBottom: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>{ze('提交信息', 'Commit Message')}: </span>
            <span>{change.commitMessage}</span>
          </div>
        )}
        
        {change.explanation && (
          <div style={{ marginBottom: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>{ze('变更说明', 'Change Description')}: </span>
            <span>{change.explanation}</span>
          </div>
        )}
        
        {change.impactAnalysis && (
          <div style={{ marginBottom: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>{ze('影响分析', 'Impact Analysis')}: </span>
            <span>{change.impactAnalysis}</span>
          </div>
        )}
        
        {change.changedCode && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              {ze('变更代码', 'Changed Code')}:
            </div>
            <ReactMarkdown
              remarkPlugins={[gfm]}
              linkTarget='_blank'
              children={`\`\`\`cpp\n${change.changedCode}\n\`\`\``}
              components={markdownComponents}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex' }}>
      {/* 左侧图标和时间线 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: '12px'
      }}>
        {/* 图标 */}
        <RootCauseIcon width={32} height={32} />

        <div style={{
          width: '2px',
          backgroundColor: '#d9d9d9',
          flexGrow: 1,
          minHeight: '20px'
        }} />
      </div>

      {/* 右侧内容 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{
          fontWeight: 'bold',
          fontSize: '18px',
          marginTop: '3px',
          marginBottom: '12px',
          color: '#333',
          lineHeight: '1.4'
        }}>
          {ze('根因结论', 'Conclusion')}
        </div>

        {/* 最可能根因 */}
        {conclusion?.rootCause && (
          <div
            style={{
              backgroundColor: '#ffd8d2',
              borderRadius: '4px',
              padding: '8px 12px',
              marginBottom: '8px',
            }}
          >
            {ze('最可能根因：', 'Most Likely Root Cause: ')}{conclusion.rootCause}
          </div>
        )}

        {/* 其他内容 */}
        <div style={{ paddingBottom: '8px', lineHeight: '1.6' }}>
          <ReactMarkdown
            remarkPlugins={[gfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
            linkTarget='_blank'
            children={content}
            components={contextualMarkdownComponents}
          />
        </div>

        {/* 代码变更信息 - 放在最后 */}
        {conclusion?.responsibleChange && renderResponsibleChange(conclusion.responsibleChange)}
      </div>
    </div>
  );
};

/**
 * Step 5: 修复建议
 * 
 * 渲染 JSON 数据中的 step4Conclusion.fixRecommendations 字段
 * 提供问题的修复建议
 * 
 * 内容包括：
 * - 紧急止血：快速缓解问题的方案
 * - 根因修复：从根本上解决问题的方案
 * - 长期加固：防止类似问题再次发生的方案
 * - 代码修复示例（修复前/修复后对比）
 * 
 * 视觉特点：
 * - 使用 SuggestionIcon 图标
 */
const Step5FixRecommendations = ({
  fixRecommendations,
  contextualMarkdownComponents,
}) => {
  let content = '';

  if (fixRecommendations?.emergencyFix && fixRecommendations.emergencyFix.after) {
    content += `1. ${ze('紧急止血', 'Emergency Fix')}: ` + fixRecommendations.emergencyFix.after.replace(/\/\/ /g, '').replace(/\n/g, ' ') + '\n';
  }
  if (fixRecommendations?.rootCauseFix) {
    content += `2. ${ze('根因修复', 'Root Cause Fix')}: ${fixRecommendations.rootCauseFix}\n`;
  }
  if (fixRecommendations?.longTermProtection) {
    content += `3. ${ze('长期加固', 'Long-term Protection')}: ${fixRecommendations.longTermProtection}\n`;
  }

  if (fixRecommendations?.emergencyFix) {
    content += '\n```cpp\n';
    content += `// ${ze('修复前', 'Before Fix')}\n`;
    content += `${fixRecommendations.emergencyFix.before}\n\n`;
    content += `// ${ze('修复后', 'After Fix')}\n`;
    content += `${fixRecommendations.emergencyFix.after}\n`;
    content += '```\n';
  }

  return (
    <div style={{ display: 'flex' }}>
      {/* 左侧图标和时间线 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: '12px'
      }}>
        {/* 图标 */}
        <SuggestionIcon width={32} height={32} />

        <div style={{
          width: '2px',
          backgroundColor: '#d9d9d9',
          flexGrow: 1,
          minHeight: '20px'
        }} />
      </div>

      {/* 右侧内容 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{
          fontWeight: 'bold',
          fontSize: '18px',
          marginTop: '3px',
          marginBottom: '12px',
          color: '#333',
          lineHeight: '1.4'
        }}>
          {ze('修复建议', 'Fix Recommendations')}
        </div>

        {/* 内容 */}
        <div style={{ paddingBottom: '8px', lineHeight: '1.6' }}>
          <ReactMarkdown
            remarkPlugins={[gfm]}
            linkTarget='_blank'
            children={content}
            components={contextualMarkdownComponents}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * AI 问题分析 JSON 格式数据渲染器（主组件）
 * 
 * @param {Object} jsonData - JSON 格式的分析数据，包含 analysis 对象
 * @param {Object} otherInfo - 其他信息，包含 quotedLogs 数组
 * @param {boolean} enableDeepAnalysis - 是否启用深度分析功能
 * @param {Function} onClickDeepAnalysis - 深度分析的回调函数
 * 
 * 工作流程：
 * 1. 解析 jsonData.analysis 获取 5 个步骤的数据
 * 2. 为每个步骤创建对应的渲染组件
 * 3. 处理引用日志链接：检测内容中的日志引用，渲染为可点击的链接按钮
 * 4. 使用 ErrorBoundary 包裹，确保渲染错误不会导致整个页面崩溃
 */
const AiIssueAnalysisJsonAnswerRenderer = ({
  jsonData,
  otherInfo,
  enableDeepAnalysis,
  onClickDeepAnalysis = () => {},
}) => {
  const onClickQuotedLog = AiDrawerHooks.useIssueAnalysisQuoteHandler();

  const quotedLogs = useMemo(() => {
    return otherInfo?.quotedLogs || [];
  }, [otherInfo]);

  /**
   * 创建包含 quotedLogs 上下文的 markdownComponents
   * 添加自定义的 'quoted-log-btn' 组件用于渲染引用日志的链接按钮
   */
  const contextualMarkdownComponents = useMemo(() => {
    return {
      ...markdownComponents,
      'quoted-log-btn': ({ node, ...props }) => {
        const quotedLogIndex = Number(props.children);

        return (
          <div style={{ display: 'inline-block'}}>
            <Tooltip
              title={ze('链接到日志位置', 'Link to log position')}
            ><CsSimpleButton
              style={{ marginLeft: '4px' }}
              className={scss.quoteLogButton}
              onClick={() => {
                const quotedLogInfo = quotedLogs[quotedLogIndex];
                if (quotedLogInfo) {
                  const { log, logCategory, crashIdOfLog, matchedLogLine } = quotedLogInfo;
                  onClickQuotedLog(crashIdOfLog, logCategory, log, matchedLogLine);
                }
              }}
            >
              <LinkOutlined />
            </CsSimpleButton></Tooltip>
          </div>
        );
      },
    };
  }, [quotedLogs, onClickQuotedLog]);

  const { analysis } = jsonData || {};

  if (!analysis) {
    return <div>Invalid analysis data structure.</div>;
  }

  return (
    <ErrorBoundary fallback={<div>Error rendering analysis results.</div>}>
      <div>
        {/* Step 1: 生成初始假设集 */}
        {analysis.step1InitialHypotheses && (
          <Step1InitialHypotheses
            hypotheses={analysis.step1InitialHypotheses}
            quotedLogs={quotedLogs}
            enableDeepAnalysis={enableDeepAnalysis}
            onClickDeepAnalysis={onClickDeepAnalysis}
            contextualMarkdownComponents={contextualMarkdownComponents}
          />
        )}

        {/* Step 2: 评估假设置信度 */}
        {analysis.step2HypothesisEvaluation && (
          <Step2HypothesisEvaluation
            evaluation={analysis.step2HypothesisEvaluation}
            hypotheses={analysis.step1InitialHypotheses}
            quotedLogs={quotedLogs}
            contextualMarkdownComponents={contextualMarkdownComponents}
          />
        )}

        {/* Step 3: 深度探索高置信假设 */}
        {analysis.step3DeepExploration && (
          <Step3DeepExploration
            exploration={analysis.step3DeepExploration}
            hypotheses={analysis.step1InitialHypotheses}
            quotedLogs={quotedLogs}
            contextualMarkdownComponents={contextualMarkdownComponents}
          />
        )}

        {/* Step 4: 根因结论 */}
        {analysis.step4Conclusion && (
          <Step4Conclusion
            conclusion={analysis.step4Conclusion}
            quotedLogs={quotedLogs}
            contextualMarkdownComponents={contextualMarkdownComponents}
          />
        )}

        {/* Step 5: 修复建议 (from step4Conclusion.fixRecommendations) */}
        {analysis.step4Conclusion?.fixRecommendations && (
          <Step5FixRecommendations
            fixRecommendations={analysis.step4Conclusion.fixRecommendations}
            contextualMarkdownComponents={contextualMarkdownComponents}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default AiIssueAnalysisJsonAnswerRenderer;

